import React from 'react';
import { useParams } from 'react-router-dom';
import { Leaf, AlertTriangle, MapPin, Camera, Edit, Share2 } from 'lucide-react';

const FoodDetailPage = () => {
  const { id } = useParams();

  // Mock data - replace with actual API call
  const food = {
    id: '1',
    name: 'Organic Apple',
    brand: 'Fresh Farms',
    barcode: '1234567890123',
    description: 'Fresh organic red apple, perfect for PKU diets',
    protein_per_100g: 0.3,
    protein_per_serving: 0.15,
    serving_size: '1 medium apple (150g)',
    exchanges_per_serving: 0.075,
    contains_aspartame: false,
    ingredients: 'Organic apple',
    nutrition_facts: 'Low in protein, high in fiber and vitamin C',
    photo_urls: [],
    locations: [
      { store: 'Whole Foods', city: 'New York', price: 2.99 },
      { store: 'Trader Joe\'s', city: 'New York', price: 1.99 },
    ],
    created_at: '2024-01-15T10:30:00Z',
    updated_at: '2024-01-15T10:30:00Z',
  };

  const isFreeFood = food.protein_exchanges < 1.0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-responsive py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-6">
          <a href="/" className="hover:text-gray-700">Home</a>
          <span className="mx-2">/</span>
          <a href="/search" className="hover:text-gray-700">Search</a>
          <span className="mx-2">/</span>
          <span className="text-gray-900">{food.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Food Header */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {food.name}
                    </h1>
                    {food.brand && (
                      <p className="text-lg text-gray-600 mb-2">
                        {food.brand}
                      </p>
                    )}
                    {food.description && (
                      <p className="text-gray-600">{food.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button className="btn btn-outline btn-sm">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </button>
                    <button className="btn btn-outline btn-sm">
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </button>
                  </div>
                </div>

                {/* Status Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {isFreeFood && (
                    <span className="badge badge-success">
                      <Leaf className="w-3 h-3 mr-1" />
                      Free Food
                    </span>
                  )}
                  {food.contains_aspartame && (
                    <span className="badge badge-warning">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Contains Aspartame
                    </span>
                  )}
                  <span className="badge badge-secondary">
                    {food.protein_per_100g}g protein/100g
                  </span>
                </div>

                {/* Barcode */}
                {food.barcode && (
                  <div className="text-sm text-gray-500">
                    Barcode: {food.barcode}
                  </div>
                )}
              </div>
            </div>

            {/* Photos */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Photos</h2>
              </div>
              <div className="card-body">
                {food.photo_urls.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {food.photo_urls.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`${food.name} photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No photos available</p>
                    <button className="btn btn-outline btn-sm mt-2">
                      Add Photo
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Nutrition Information */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Nutrition Information</h2>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Protein Content</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Per 100g:</span>
                        <span className="font-medium">{food.protein_per_100g}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Per serving:</span>
                        <span className="font-medium">{food.protein_per_serving}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Serving size:</span>
                        <span className="font-medium">{food.serving_size}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Exchanges:</span>
                        <span className="font-medium">{food.exchanges_per_serving}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Dietary Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">Free Food:</span>
                        <span className={isFreeFood ? 'text-success-600' : 'text-gray-900'}>
                          {isFreeFood ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">Aspartame:</span>
                        <span className={food.contains_aspartame ? 'text-warning-600' : 'text-success-600'}>
                          {food.contains_aspartame ? 'Contains' : 'Free'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Nutrition Info */}
                {food.nutrition_facts && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-2">Additional Information</h3>
                    <p className="text-gray-600">{food.nutrition_facts}</p>
                  </div>
                )}

                {/* Ingredients */}
                {food.ingredients && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-2">Ingredients</h3>
                    <p className="text-gray-600">{food.ingredients}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Locations */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Where to Buy</h2>
              </div>
              <div className="card-body">
                {food.locations.length > 0 ? (
                  <div className="space-y-4">
                    {food.locations.map((location, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <MapPin className="w-5 h-5 text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900">{location.store}</div>
                            <div className="text-sm text-gray-500">{location.city}</div>
                          </div>
                        </div>
                        {location.price && (
                          <div className="text-lg font-semibold text-gray-900">
                            ${location.price}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No location information available</p>
                    <button className="btn btn-outline btn-sm mt-2">
                      Add Location
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
              </div>
              <div className="card-body space-y-3">
                <button className="btn btn-primary w-full">
                  Add to My Foods
                </button>
                <button className="btn btn-outline w-full">
                  Report Incorrect Info
                </button>
                <button className="btn btn-outline w-full">
                  Suggest Location
                </button>
              </div>
            </div>

            {/* Food Stats */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Food Stats</h2>
              </div>
              <div className="card-body space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Added:</span>
                  <span className="text-gray-900">
                    {new Date(food.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Updated:</span>
                  <span className="text-gray-900">
                    {new Date(food.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Views:</span>
                  <span className="text-gray-900">1,234</span>
                </div>
              </div>
            </div>

            {/* Similar Foods */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Similar Foods</h2>
              </div>
              <div className="card-body">
                <p className="text-gray-500 text-sm">
                  Find similar foods with comparable protein content and nutritional profiles.
                </p>
                <button className="btn btn-outline btn-sm w-full mt-3">
                  Browse Similar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodDetailPage;
